/**
 * ToolPickerModal component.
 * @module components/Widgets/OrderToolsWidget/ToolPickerModal
 */

import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import {
  Input,
  Modal,
  Table,
  TableHeader,
  TableRow,
  TableBody,
  TableCell,
  TableHeaderCell,
} from 'semantic-ui-react';

import { Icon } from '@plone/volto/components';
import { getContent } from '@plone/volto/actions';

import downSVG from '@plone/volto/icons/down-key.svg';
import upSVG from '@plone/volto/icons/up-key.svg';
import clearSVG from '@plone/volto/icons/clear.svg';

const LIST_SUBREQUEST = 'tool-picker-list';
const TOOLS_PATH = '/tools';

/**
 * ToolPickerModal
 * Lets the user search for and select an existing tool, from within a
 * modal (used by OrderToolsWidget).
 */
const ToolPickerModal = ({ open, onClose, onSelect, selectedId }) => {
  const dispatch = useDispatch();

  // Filtering
  const [fullcode, setFullcode] = useState('');
  const [name, setName] = useState('');
  const [category, setCategory] = useState('');

  // Sorting
  const [sort, setSort] = useState('fullcode');
  const [dir, setDir] = useState('asc');

  useEffect(() => {
    if (open) {
      dispatch(getContent(TOOLS_PATH, null, LIST_SUBREQUEST));
    }
  }, [open, dispatch]);

  const listRequest = useSelector(
    (state) => state.content.subrequests?.[LIST_SUBREQUEST],
  );

  const filterField = (items, field, value) =>
    value === ''
      ? items
      : items.filter(
          (item) =>
            item[field] &&
            item[field].toLowerCase().indexOf(value.toLowerCase()) !== -1,
        );

  let items = (listRequest?.data?.items || []).map((item) => ({
    ...item,
    fullcode: item.code_variant
      ? `${item.code}.${item.code_variant}`
      : item.code,
  }));
  items = filterField(items, 'fullcode', fullcode);
  items = filterField(items, 'name', name);
  items = filterField(items, 'category', category);
  items = [...items].sort((a, b) =>
    (dir === 'asc' ? a[sort] > b[sort] : a[sort] < b[sort]) ? 1 : -1,
  );

  const onSort = (value) => {
    if (sort === value) {
      setDir(dir === 'asc' ? 'desc' : 'asc');
    } else {
      setSort(value);
      setDir('asc');
    }
  };

  const SortIcon = () => (
    <Icon
      name={dir === 'desc' ? upSVG : downSVG}
      className="icon"
      size="20px"
    />
  );

  return (
    <Modal
      size="large"
      open={open}
      onClose={onClose}
      className="contact-picker-modal tool-picker-modal"
    >
      <Modal.Header className="contact-picker-header">
        <div className="contact-picker-header-action" />
        <div className="contact-picker-header-title">Select a tool</div>
        <div className="contact-picker-header-action">
          <button
            type="button"
            className="item ui noborder button"
            onClick={onClose}
            aria-label="Close"
          >
            <Icon name={clearSVG} size="20px" />
          </button>
        </div>
      </Modal.Header>
      <Modal.Content>
        <div className="scrollable-table">
          <Table striped celled selectable>
            <TableHeader>
              <TableRow className="tools-header">
                <TableHeaderCell onClick={() => onSort('fullcode')} width="2">
                  Code {sort === 'fullcode' && <SortIcon />}
                </TableHeaderCell>
                <TableHeaderCell onClick={() => onSort('name')} width="6">
                  Name {sort === 'name' && <SortIcon />}
                </TableHeaderCell>
                <TableHeaderCell onClick={() => onSort('category')} width="4">
                  Category {sort === 'category' && <SortIcon />}
                </TableHeaderCell>
              </TableRow>
              <TableRow className="tools-filter">
                <TableHeaderCell>
                  <div className="filter-input">
                    <Input
                      value={fullcode}
                      placeholder="Filter code"
                      autoCorrect="off"
                      spellCheck="false"
                      onChange={(e) =>
                        setFullcode(e.target.value.toUpperCase())
                      }
                    />
                    {fullcode && (
                      <Icon
                        name={clearSVG}
                        size="16px"
                        className="clear-icon"
                        onClick={() => setFullcode('')}
                      />
                    )}
                  </div>
                </TableHeaderCell>
                <TableHeaderCell>
                  <div className="filter-input">
                    <Input
                      value={name}
                      placeholder="Filter name"
                      autoCorrect="off"
                      spellCheck="false"
                      onChange={(e) => setName(e.target.value)}
                    />
                    {name && (
                      <Icon
                        name={clearSVG}
                        size="16px"
                        className="clear-icon"
                        onClick={() => setName('')}
                      />
                    )}
                  </div>
                </TableHeaderCell>
                <TableHeaderCell>
                  <div className="filter-input">
                    <Input
                      value={category}
                      placeholder="Filter category"
                      autoCorrect="off"
                      spellCheck="false"
                      onChange={(e) => setCategory(e.target.value)}
                    />
                    {category && (
                      <Icon
                        name={clearSVG}
                        size="16px"
                        className="clear-icon"
                        onClick={() => setCategory('')}
                      />
                    )}
                  </div>
                </TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((item) => (
                <TableRow
                  key={item.id}
                  className={`clickable-row${
                    selectedId && item.id === selectedId ? ' selected-row' : ''
                  }`}
                  onClick={() => onSelect(item.id)}
                >
                  <TableCell>{item.fullcode}</TableCell>
                  <TableCell>
                    {item.name}
                    {item.name_variant ? ` (${item.name_variant})` : ''}
                  </TableCell>
                  <TableCell>{item.category}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Modal.Content>
    </Modal>
  );
};

ToolPickerModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  selectedId: PropTypes.string,
};

export default ToolPickerModal;
